"use client"

import type React from "react"
import { useState, useEffect, useMemo, useRef, useSyncExternalStore } from "react"
import { Input } from "./ui/input"
import { Button } from "./ui/button"
import { Label } from "./ui/label"
import { Calendar } from "./ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { ModalSearchableSelect } from "./ui/modal-searchable-select"
import { CalendarIcon, Search, Briefcase, Loader2, Target } from "lucide-react"
import { format } from "date-fns"
import { cn } from "./lib/utils"
import { createApproach, getApproachApiErrorMessage, isBlockedPhoneError, checkApproachPhoneBlocked } from "../services/approachesapi"
import { getJobs, getJobsForSelection } from "../services/jobsapi"
import { DayPicker } from "react-day-picker"
import "react-day-picker/dist/style.css"
import { useNavigate } from "react-router-dom"
import { toast } from "react-hot-toast"
import BlockedPhonePopup from "./BlockedPhonePopup"

/** True for most phones/tablets; avoids programmatic focus that opens the soft keyboard and collapses Radix Select on Android Chrome. */
function usePointerCoarse() {
  return useSyncExternalStore(
    (onChange) => {
      const mq = window.matchMedia("(pointer: coarse)")
      mq.addEventListener("change", onChange)
      return () => mq.removeEventListener("change", onChange)
    },
    () => window.matchMedia("(pointer: coarse)").matches,
    () => false
  )
}

const sourceOptions = [
  { label: "EarlyJobs", value: "EarlyJobs" },
  { label: "LinkedIn Jobs", value: "LinkedIn Jobs" },
  { label: "Apna.co", value: "Apna.co" },
  { label: "Naukri.com", value: "Naukri.com" },
  { label: "Jobhai.com", value: "Jobhai.com" },
  { label: "Indeed India", value: "Indeed India" }, 
  { label: "Foundit", value: "Foundit" },
  { label: "Shine.com", value: "Shine.com" },
  { label: "TimesJobs", value: "TimesJobs" },
  { label: "Glassdoor", value: "Glassdoor" },
  { label: "Google Jobs", value: "Google Jobs" },
  { label: "Freshersworld", value: "Freshersworld" },
  { label: "Cutshort", value: "Cutshort" },
  { label: "Hirect", value: "Hirect" },
  { label: "Hirist", value: "Hirist" },
  { label: "LetsIntern", value: "LetsIntern" },
  { label: "JobsForHer", value: "JobsForHer" },
  { label: "WorkIndia", value: "WorkIndia" },
  { label: "Wellfound", value: "Wellfound" },
  { label: "QuikrJobs", value: "QuikrJobs" },
  { label: "Jobsora", value: "Jobsora" },
  { label: "WisdomJobs", value: "WisdomJobs" },
  { label: "Jagran Josh Jobs", value: "Jagran Josh Jobs" },
  { label: "CareerIndia", value: "CareerIndia" },
  { label: "CareerAge", value: "CareerAge" },
  { label: "Neuvoo / Talent.com", value: "Neuvoo / Talent.com" },
  { label: "Recruit.net", value: "Recruit.net" },
  { label: "WhatJobs.com", value: "WhatJobs.com" },
  { label: "JobSquare", value: "JobSquare" },
  { label: "SarkariNaukri.com", value: "SarkariNaukri.com" },
  { label: "FreeJobAlert.com", value: "FreeJobAlert.com" },
  { label: "RozgarIndia", value: "RozgarIndia" },
  { label: "JobGranth", value: "JobGranth" },
  { label: "NaukriHub", value: "NaukriHub" },
  { label: "MonsterGulf", value: "MonsterGulf" },
  { label: "CareerJet India", value: "CareerJet India" },
  { label: "JobSarkari", value: "JobSarkari" },
  { label: "Times Ascent", value: "Times Ascent" },
  { label: "Updazz", value: "Updazz" },
  { label: "Instahyre", value: "Instahyre" },
  { label: "PlacementIndia", value: "PlacementIndia" },
  { label: "SarkariResult.com", value: "SarkariResult.com" },
  { label: "FreshersLive", value: "FreshersLive" },
  { label: "JobStreet India", value: "JobStreet India" },
  { label: "PlacementIndia.com", value: "PlacementIndia.com" },
  { label: "Youth4Work", value: "Youth4Work" },
  { label: "JobNukkad", value: "JobNukkad" },
  { label: "TeamLease Jobs", value: "TeamLease Jobs" },
  { label: "Babajob", value: "Babajob" },
  { label: "Jooble India", value: "Jooble India" },
  { label: "ClickIndia Jobs", value: "ClickIndia Jobs" },
  { label: "Olx Jobs", value: "Olx Jobs" },
  { label: "Internshala", value: "Internshala" },
  { label: "Twenty19", value: "Twenty19" },
  { label: "KillerLaunch", value: "KillerLaunch" },
  { label: "Placement Season", value: "Placement Season" },
  { label: "HelloIntern", value: "HelloIntern" },
  { label: "Edugorilla Jobs", value: "Edugorilla Jobs" },
  { label: "RecruitNXT", value: "RecruitNXT" },
  { label: "FirstNaukri", value: "FirstNaukri" },
  { label: "JobBuzz", value: "JobBuzz" },
  { label: "Peeljobs", value: "Peeljobs" },
  { label: "MyJobStreet", value: "MyJobStreet" },
  { label: "DreamStarts", value: "DreamStarts" },
  { label: "BigShyft", value: "BigShyft" },
  { label: "iimjobs", value: "iimjobs" },
  { label: "Engineeristic", value: "Engineeristic" },
  { label: "PharmaTutor Jobs", value: "PharmaTutor Jobs" },
  { label: "MedJobsIndia", value: "MedJobsIndia" },
  { label: "BioJobIndia", value: "BioJobIndia" },
  { label: "CXOJobs", value: "CXOJobs" },
  { label: "JobSahaayata", value: "JobSahaayata" },
  { label: "WorkIndia.in", value: "WorkIndia.in" },
  { label: "QuickJobs", value: "QuickJobs" },
  { label: "Youth4Work Jobs", value: "Youth4Work Jobs" },
  { label: "Rozgar Bazaar", value: "Rozgar Bazaar" },
  { label: "MahaJobs (Maharashtra Govt.)", value: "MahaJobs (Maharashtra Govt.)" },
  { label: "TNVelaiPortal (Tamil Nadu Govt.)", value: "TNVelaiPortal (Tamil Nadu Govt.)" },
  { label: "Kerala PSC Jobs Portal", value: "Kerala PSC Jobs Portal" },
  { label: "MP Rojgar Portal", value: "MP Rojgar Portal" },
  { label: "Haryana Rojgar Portal", value: "Haryana Rojgar Portal" },
  { label: "UP Rojgar Sangam", value: "UP Rojgar Sangam" },
  { label: "SkillConnect Portal (Karnataka Govt.)", value: "SkillConnect Portal (Karnataka Govt.)" },
  { label: "Punjab Ghar Ghar Rozgar", value: "Punjab Ghar Ghar Rozgar" },
  { label: "Himachal Rojgar Portal", value: "Himachal Rojgar Portal" },
  { label: "Chhattisgarh Rojgar Portal", value: "Chhattisgarh Rojgar Portal" },
  { label: "Odisha Employment Portal", value: "Odisha Employment Portal" },
  { label: "Assam Career", value: "Assam Career" },
  { label: "SarkariExam.com", value: "SarkariExam.com" },
  { label: "SarkariJobFind.com", value: "SarkariJobFind.com" },
  { label: "EmploymentNews.gov.in", value: "EmploymentNews.gov.in" },
  { label: "JobMela.in", value: "JobMela.in" },
  { label: "Rocket.in", value: "Rocket.in" },
  { label: "GigIndia", value: "GigIndia" },
  { label: "FlexingIt", value: "FlexingIt" },
  { label: "Truelancer", value: "Truelancer" },
  { label: "WorkNHire", value: "WorkNHire" },
  { label: "Freelancer.in", value: "Freelancer.in" },
  { label: "Upwork India", value: "Upwork India" },
  { label: "Fiverr India", value: "Fiverr India" },
  { label: "Others", value: "Others" }
]


const statusOptions = [
  { label: "Call Back", value: "Call Back" },
  {label:"Interested",value:"Interested"},
  { label: "Schedule", value: "Schedule" },
  { label: "Ringing Not Reachable", value: "Ringing Not Reachable" },
  { label: "Not Relevant", value: "Not Relevant" },
  { label: "Not Reachable", value: "Not Reachable" },
  { label: "Not Interested", value: "Not Interested" },
  { label: "Salary Issue", value: "Salary Issue" },
  { label: "Incorrect Number", value: "Incorrect Number" },
  { label: "DND", value: "DND" },
  { label: "Switched Off", value: "Switched Off" },
  { label: "Location Issue", value: "Location Issue" },
  { label: "Already Employed", value: "Already Employed" },
]

interface AddUserModalProps {
  isOpen: boolean
  onClose: () => void
  onAdd?: (user: any) => boolean | Promise<boolean>
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onAdd }) => {
  const [form, setForm] = useState({
    name: "",
    number: "",
    email: "",
    qualifications: "",
    city: "",
    source: "",
    status: "",
    date: "",
    time: "",
    remarks: "",
    jobTitle: "",
  })
  const [calendarDate, setCalendarDate] = useState<Date | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)

  const [jobs, setJobs] = useState<{  _id: string; title: string; companyName?: string; location?: string }[]>([])
  const [jobLoading, setJobLoading] = useState(false)
  const [jobsError, setJobsError] = useState<string | null>(null)
  const [jobSearchTerm, setJobSearchTerm] = useState("")
  const[jobId,setJobId]= useState("")
  const [isJobSelectOpen, setIsJobSelectOpen] = useState(false)
  const jobSearchInputRef = useRef<HTMLInputElement>(null)
  const [phoneError, setPhoneError] = useState<string>("")
  const [blockedPopupOpen, setBlockedPopupOpen] = useState(false)
  const [blockedPopupMessage, setBlockedPopupMessage] = useState("")
  const pointerCoarse = usePointerCoarse()

  const navigate = useNavigate()

  useEffect(() => {
    if (isOpen) {
      setJobLoading(true)
      setJobsError(null)
      getJobs()
        .then((data) => {
          const jobsData =
            data.data.jobs?.map((job: any) => ({
              _id: job.id,
              title: job.title,
              companyName: job.companyName,
              location: job.location,
            })) || []
          setJobs(jobsData)
        })
        .catch((error) => {
          console.error("Failed to fetch jobs:", error)
          setJobsError("Failed to load job positions")
          setJobs([])
        })
        .finally(() => setJobLoading(false))
    } else {
      // Reset job search state when modal closes
      setJobSearchTerm("")
      setIsJobSelectOpen(false)
    }
  }, [isOpen])
useEffect(() => {
  // Debounce the search to prevent losing focus on every keystroke
  const timeoutId = setTimeout(() => {
    const filteredJobs = async () => {
      // Store if the input was focused before the API call
      const wasInputFocused = document.activeElement === jobSearchInputRef.current
      
      try {
        const response = await getJobsForSelection({ search: jobSearchTerm })
        const rawJobs = (response?.data?.jobs || []) as any[]
        setJobs(
          rawJobs
            .map((job: any) => ({
              _id: String(job._id || job.id || ""),
              title: job.title,
              companyName: job.companyName,
              location: job.location,
            }))
            .filter((job) => job._id.length > 0)
        )
        
        // Restore focus after state update if it was focused before (skip on touch: keyboard resize closes Select on Android)
        if (wasInputFocused && jobSearchInputRef.current && !pointerCoarse) {
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            setTimeout(() => {
              jobSearchInputRef.current?.focus()
            }, 0)
          })
        }
      } catch (err) {
        console.error("Failed to fetch filtered jobs:", err)
        // Restore focus even on error
        if (wasInputFocused && jobSearchInputRef.current && !pointerCoarse) {
          requestAnimationFrame(() => {
            jobSearchInputRef.current?.focus()
          })
        }
      }
    }
    filteredJobs()
  }, 300) // 300ms debounce delay

  return () => clearTimeout(timeoutId)
}, [jobSearchTerm, pointerCoarse])

  // Focus the search input when the select opens (omit on touch — soft keyboard triggers viewport churn and Radix closes)
  useEffect(() => {
    if (pointerCoarse || !isJobSelectOpen || !jobSearchInputRef.current) return
    const t = window.setTimeout(() => {
      jobSearchInputRef.current?.focus()
    }, 100)
    return () => window.clearTimeout(t)
  }, [isJobSelectOpen, pointerCoarse])

  // Restore focus after jobs state updates when typing on desktop only
  useEffect(() => {
    if (pointerCoarse || !isJobSelectOpen || !jobSearchInputRef.current || jobSearchTerm.length === 0) return
    requestAnimationFrame(() => {
      setTimeout(() => {
        jobSearchInputRef.current?.focus()
      }, 0)
    })
  }, [jobs, isJobSelectOpen, jobSearchTerm, pointerCoarse])

  // const filteredJobs = useMemo(() => {
  //   if (!jobSearchTerm.trim()) return jobs

  //   return jobs.filter((job) => {
  //     const searchLower = jobSearchTerm.toLowerCase()
  //     return (
  //       job.title?.toLowerCase().includes(searchLower) ||
  //       job.companyName?.toLowerCase().includes(searchLower) ||
  //       job.location?.toLowerCase().includes(searchLower)
  //     )
  //   })
  // }, [jobs, jobSearchTerm])



const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault()
  setLoading(true)
  try {
    const payload: any = {
      name: form.name,
      phone: form.number,
      email: form.email,
      qualifications: form.qualifications,
      city: form.city,
      source: form.source,
      callStatus: form.status,
      remarks: form.remarks,
    }

    const effectiveJobId = (jobId || form.jobTitle || "").trim()
    if (effectiveJobId && !effectiveJobId.startsWith("job-")) {
      payload.jobId = effectiveJobId
    }

    if (form.status === "Call Back") {
      payload.callBackTime = `${form.date}T${form.time}`
    }

    // If status is Schedule, navigate to add-candidate form and defer approach creation
    if (form.status === "Schedule") {
      try {
        await checkApproachPhoneBlocked(form.number)
      } catch (error) {
        if (isBlockedPhoneError(error)) {
          setBlockedPopupMessage(getApproachApiErrorMessage(error))
          setBlockedPopupOpen(true)
          return
        }
        throw error
      }
      // Close current modal immediately
      onClose()
      
      // Navigate with state carrying approach payload and all available prefills
      navigate("/add-candidate", {
        state: {
          fromApproaches: true,
          approachPayload: payload,
          prefill: {
            // All available form fields for prefill
            name: form.name,
            email: form.email,
            phone: form.number,
            highestQualification: form.qualifications,
            city: form.city,
            jobId: (jobId || form.jobTitle || "").trim(),
            dateOfBirth: "",
            skills: [],
            workExperience: 0,
          },
        },
      })
      setForm({
        name: "",
        number: "",
        email: "",
        qualifications: "",
        city: "",
        source: "",
        status: "",
        date: "",
        time: "",
        jobTitle: "",
        remarks: "",
      })
      setJobId("")
      setCalendarDate(undefined)
      setJobSearchTerm("")
      setPhoneError("")
      setLoading(false)
      return
    }

    // Create approach immediately for non-schedule statuses
    await createApproach(payload)

    setForm({
      name: "",
      number: "",
      email: "",
      qualifications: "",
      city: "",
      source: "",
      status: "",
      date: "",
      time: "",
      jobTitle: "",
      remarks: "",
    })
    setJobId("")
    setCalendarDate(undefined)
    setJobSearchTerm("")
    setPhoneError("")
    onClose()
  } catch (error) {
    console.error("Error adding user:", error)
    if (isBlockedPhoneError(error)) {
      setBlockedPopupMessage(getApproachApiErrorMessage(error))
      setBlockedPopupOpen(true)
    }
   
  } finally {
    setLoading(false)
  }
}

  const handleStatusChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      status: value,
      date: "",
      time: "",
    }))
    setCalendarDate(undefined)
  }

  const handleDateSelect = (date: Date | undefined) => {
    setCalendarDate(date)
    setForm((prev) => ({
      ...prev,
      date: date ? format(date, "yyyy-MM-dd") : "",
    }))
    setIsCalendarOpen(false)
  }

  const isCallback = form.status === "Call Back"
  const effectiveJobId = (jobId || form.jobTitle || "").trim()
  const hasValidJob =
    effectiveJobId.length > 0 && !effectiveJobId.startsWith("job-")
  const isFormValid =
    form.name.trim().length > 0 &&
    form.number.length === 10 &&
    form.source &&
    form.status &&
    hasValidJob &&
    (!isCallback || (form.date && form.time)) &&
    !phoneError

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-2xl font-semibold text-center">Add New Approach</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
           <div className="space-y-3 max-w-full">
              <Label htmlFor="jobId" className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <Target className="h-4 w-4 text-gray-500" />
                Job Position <span className="text-red-500">*</span>
              </Label>
              <div className="relative w-full">
                <Select
                  value={form.jobTitle}
                  open={isJobSelectOpen}
                  onOpenChange={(open) => {
                    // Soft keyboard / viewport resize on mobile fires close while search is focused
                    if (
                      !open &&
                      jobSearchInputRef.current &&
                      (document.activeElement === jobSearchInputRef.current ||
                        jobSearchInputRef.current.contains(document.activeElement))
                    ) {
                      return
                    }
                    setIsJobSelectOpen(open)
                    if (!open) setJobSearchTerm("")
                  }}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, jobTitle: value }))
                    const selectedJob = jobs.find((job) => job._id === value)
                    // Keep jobId in sync with selection (_id vs id mismatch handled via normalized jobs; value is always real id from SelectItem)
                    setJobId(selectedJob?._id ?? "")
                    setIsJobSelectOpen(false)
                    setJobSearchTerm("")
                  }}
                  disabled={jobLoading}
                >
                  <SelectTrigger className="h-11 w-full rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/70 font-medium shadow-sm hover:shadow-md transition-all duration-200">
                    <SelectValue
                      placeholder={
                        jobLoading ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading available positions...</span>
                          </div>
                        ) : jobsError ? (
                          "Error loading jobs - Click to retry"
                        ) : jobs.length === 0 ? (
                          "No positions available"
                        ) : (
                          "Search and select a job position"
                        )
                      }
                    />
                  </SelectTrigger>
                  <SelectContent 
                    className="rounded-xl max-h-80 border-2 shadow-2xl w-[var(--radix-select-trigger-width)]"
                    position="popper"
                    onCloseAutoFocus={(e) => {
                      e.preventDefault()
                    }}
                    onPointerDownOutside={(e) => {
                      const target = e.target as Node | null
                      if (target && jobSearchInputRef.current?.contains(target)) {
                        e.preventDefault()
                      }
                    }}
                  >
                    <div
                      className="p-3 border-b border-gray-200"
                      onPointerDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input
                          ref={jobSearchInputRef}
                          placeholder="Search jobs by title, company, or location..."
                          inputMode="search"
                          enterKeyHint="search"
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="none"
                          spellCheck={false}
                          value={jobSearchTerm}
                          onChange={(e) => {
                            setJobSearchTerm(e.target.value)
                            setIsJobSelectOpen(true)
                          }}
                          onPointerDown={(e) => {
                            // Required for mobile: stop Radix from treating tap as dismiss / stealing focus
                            e.preventDefault()
                            e.stopPropagation()
                            jobSearchInputRef.current?.focus()
                          }}
                          onTouchStart={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation()
                            if (e.key === "Enter") {
                              e.preventDefault()
                            }
                          }}
                          className="pl-10 h-10 rounded-lg border-gray-200 focus:border-orange-400 focus:ring-orange-400"
                        />
                      </div>
                    </div>

                    {jobLoading ? (
                      <div className="flex items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
                          <span className="text-sm font-medium text-gray-600">Loading positions...</span>
                        </div>
                      </div>
                    ) : jobsError ? (
                      <div className="p-6 text-center">
                        <div className="flex flex-col gap-3">
                          <div className="text-red-600 font-medium">{jobsError}</div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.reload()}
                            className="self-center bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                          >
                            🔄 Retry Loading
                          </Button>
                        </div>
                      </div>
                    ) : !Array.isArray(jobs) || jobs.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <Briefcase className="h-8 w-8 text-gray-300" />
                          <span className="font-medium">
                            {!Array.isArray(jobs) ? "Invalid job data format" : "No positions available"}
                          </span>
                        </div>
                      </div>
                    ) : jobs.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <Search className="h-8 w-8 text-gray-300" />
                          <span className="font-medium">No jobs match your search</span>
                          <span className="text-sm">Try different keywords</span>
                        </div>
                      </div>
                    ) : (
                      jobs
                        .map((job, index) => {
                          if (!job) {
                            console.warn("Invalid job object:", job)
                            return null
                          }

                          const jobId = job._id || `job-${index}`
                          const jobTitle = job.title || "Untitled Job"
                          const displayText = job.companyName ? `${jobTitle} - ${job.companyName}` : jobTitle

                          return (
                            <SelectItem
                              key={jobId}
                              value={jobId}
                              className="cursor-pointer hover:bg-orange-50 focus:bg-orange-50"
                            >
                              <div className="flex flex-col items-start">
                                <span className="font-medium">{displayText}</span>
                                {job.location && <span className="text-xs text-gray-500">{job.location}</span>}
                              </div>
                            </SelectItem>
                          )
                        })
                        .filter(Boolean)
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="Enter full name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className="h-11"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="number" className="text-sm font-medium text-gray-700">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="number"
                placeholder="Enter phone number"
                value={form.number}
                type="tel"
                inputMode="numeric"
                pattern="^[0-9]*$"
                maxLength={10}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/[^0-9]/g, "")
                  setForm((prev) => ({ ...prev, number: digitsOnly }))
                  
                  // Validate phone number length
                  if (digitsOnly.length > 0 && digitsOnly.length < 10) {
                    setPhoneError("Phone number must be exactly 10 digits")
                  } else if (digitsOnly.length === 10) {
                    setPhoneError("")
                  } else if (digitsOnly.length === 0) {
                    setPhoneError("")
                  }
                }}
                onBlur={() => {
                  if (form.number.length > 0 && form.number.length < 10) {
                    setPhoneError("Phone number must be exactly 10 digits")
                  } else if (form.number.length === 10) {
                    setPhoneError("")
                  } else if (form.number.length === 0) {
                    setPhoneError("Phone number is required")
                  }
                }}
                className={`h-11 ${phoneError ? 'border-red-500' : ''}`}
                required
              />
              {phoneError && (
                <p className="text-xs text-red-500 mt-1">{phoneError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email address"
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="qualifications" className="text-sm font-medium text-gray-700">
                Qualifications 
              </Label>
              <Input
                id="qualifications"
                placeholder="Enter qualifications"
                value={form.qualifications}
                onChange={(e) => setForm((prev) => ({ ...prev, qualifications: e.target.value }))}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city" className="text-sm font-medium text-gray-700">
                City
              </Label>
              <Input
                id="city"
                placeholder="Enter city"
                value={form.city}
                onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Source <span className="text-red-500">*</span>
              </Label>
              <ModalSearchableSelect
                options={sourceOptions}
                value={form.source}
                onValueChange={(value) => setForm((prev) => ({ ...prev, source: value }))}
                placeholder="Select source"
                searchPlaceholder="Search job portals..."
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Status <span className="text-red-500">*</span>
              </Label>
              <Select value={form.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

           
          </div>
         
               <div className="space-y-2">
              <Label htmlFor="remarks" className="text-sm font-medium text-gray-700">
                Remarks
              </Label>
              <Input
                id="remarks"
                placeholder="Enter Remarks"
                value={form.remarks}
                onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))}
                className="h-11"
                
              />
            </div>
            
          
          {isCallback && (
            <div className="space-y-4 p-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-orange-600" />
                Callback Date & Time
              </h3>

              {/* Show selected date and time for Call Back */}
              {form.date && form.time && (
                <div className="text-sm text-orange-700 font-medium mb-2">
                  Selected: {format(new Date(form.date), "dd MMM yyyy")} at {form.time}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl shadow p-2">
                  <DayPicker
                    mode="single"
                    selected={calendarDate}
                    onSelect={handleDateSelect}
                    initialFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Time</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm((prev) => ({ ...prev, time: e.target.value }))}
                    className="h-11"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose} className="h-11 px-6 bg-transparent">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid || loading}
              className="h-11 px-6 bg-[#EC6E51] hover:bg-[#d65f47] text-white disabled:opacity-50"
            >
              {loading ? "Adding..." : "Add User"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    <BlockedPhonePopup
      open={blockedPopupOpen}
      message={blockedPopupMessage}
      onClose={() => setBlockedPopupOpen(false)}
    />
    </>
  )
}

export default AddUserModal